<script>
    import { selectedItem, currentUser } from '../../store/store.js';
    import { fetchBrf, displayInt, tooltip } from '../../utils/utils.js';
    import Logotype from '../Logotype.svelte';
    import { Link } from 'svelte-routing';
    import './shared.css';

    function displayNumberOfApartments(itemData) {
        if (itemData.number_of_apartments) {
            return itemData.number_of_apartments;
        }
        if (itemData.data === null) {
            return "Uppgifter saknas";
        }
        if (itemData.data) {
            for (const element of itemData.data) {
                if (element.apartments) {
                    return element.apartments;
                }
            }
        }
        return "-";
    }

    function displayOptionalInt(value) {
        return value != null ? displayInt(value) : '-';
    }

    function displayOptionalBoolean(value, trueText = 'Ja', falseText = 'Nej', nullText = '-') {
        if (value === true) return trueText;
        if (value === false) return falseText;
        return nullText;
    }

    let fileInput = null;
    let selectedFile = null;
    let loading = false;
    let error = null;
    let uploadedForCoreId = null;

    $: imageFileName = $selectedItem?.image_file_name || $selectedItem?.brf_image || null;
    $: imageUrl = imageFileName ? `https://storage.googleapis.com/brf_images/${imageFileName}` : null;
    $: hasExistingImage = !!imageFileName;
    $: hasUploaded = uploadedForCoreId === $selectedItem?.core_id;
    $: isProOrPlus = $currentUser?.subscription_tier === 'Pro' || $currentUser?.subscription_tier === 'Plus';
    $: isLoggedIn = !!$currentUser?.accessToken;
    $: subscriptionLinkTo = isLoggedIn ? '/profile' : '/login';

    $: if ($selectedItem && uploadedForCoreId !== $selectedItem.core_id) {
        selectedFile = null;
        error = null;
        if (fileInput) fileInput.value = '';
    }

    async function handleFileChange(event) {
        const file = event.target.files[0];
        if (!file) return;
        
        selectedFile = file;
        error = null;
        
        if ($selectedItem?.core_id) {
            await uploadImage();
        }
    }

    function triggerFileInput() {
        if (!hasUploaded && !loading) {
            fileInput.click();
        }
    }

    async function uploadImage() {
        if (!selectedFile || !$selectedItem?.core_id) {
            error = 'Välj en fil och kontrollera att core_id är tillgängligt';
            return;
        }

        loading = true;
        error = null;

        try {
            const formData = new FormData();
            formData.append('file', selectedFile, selectedFile.name);
            const url = `https://boanalys-backend-324523837899.europe-north1.run.app/generate_image_of_brf?core_id=${encodeURIComponent($selectedItem.core_id)}`;

            const response = await fetch(url, {
                method: 'POST',
                body: formData
            });

            if (!response.ok) {
                let errorMessage = 'Uppladdning misslyckades';
                try {
                    const errorData = await response.json();
                    errorMessage = errorData.detail || errorMessage;
                } catch (e) {
                    const errorText = await response.text();
                    errorMessage = errorText || errorMessage;
                }
                throw new Error(errorMessage);
            }

            const data = await response.json();
            uploadedForCoreId = $selectedItem?.core_id;
            
            await fetchBrf($selectedItem.core_id);
            
        } catch (err) {
            error = err.message;
        } finally {
            loading = false;
        }
    }
</script>

<div class="building-wrapper">
    <div class="building-box">
        <div class="header-card">
            <h5><i class="fa-solid fa-building fa-sm icon-detail"></i> Fastighet</h5>
        </div>
        <div class="building-content-grid">
            {#if $selectedItem?.living_area_sqm}
                <div class="metric-card metric-total-boyta">
                    <div class="metric-header">
                        <h6><i class="fa-solid fa-ruler-combined fa-sm icon-detail"></i> Total boyta</h6>
                        <div class="tooltip-container" use:tooltip>
                            <span class="info-icon"></span>
                            <div class="tooltip-content">
                                <p>Om den totala boytan ej anges i årsredovisningen har denna siffra beräknats genom att dividera Totala Avgiftsinkomsten med Avgift per kvm.</p>
                            </div>
                        </div>
                    </div>
                    <div class="metric-value">
                        { displayInt($selectedItem.living_area_sqm) } kvm
                    </div>
                </div>
            {/if}

            {#if $selectedItem?.number_of_apartments || $selectedItem?.data}
                <div class="metric-card metric-antal-lagenheter">
                    <div class="metric-header">
                        <h6><i class="fa-solid fa-building fa-sm icon-detail"></i> Antal lägenheter</h6>
                        <div class="tooltip-container" use:tooltip>
                            <span class="info-icon"></span>
                            <div class="tooltip-content">
                                <p>Det finns ingen exakt regel för vad som gör en bostadsrättsförening med få lägenheter "bra" eller "dålig", men här är några allmänna riktlinjer:</p>
                                <p><strong>Sammanfattning av vad man bör tänka på:</strong></p>
                                <ul>
                                    <li><strong>Fördelar med få lägenheter:</strong> Nära gemenskap, större individuellt inflytande, snabbare beslutsprocesser.</li>
                                    <li><strong>Nackdelar med få lägenheter:</strong> Ekonomisk sårbarhet, mer administrativt ansvar per medlem, färre kompetenser i föreningen.</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                    <div class="metric-value">
                        { displayNumberOfApartments($selectedItem) } st
                    </div>
                </div>
            {/if}

            <div class="image-section-center">
                <div class="image-container">
                    {#if hasExistingImage}
                        <img src={imageUrl} alt="BRF bild" />
                    {:else}
                        <div class="image-upload-wrapper">
                            <input 
                                type="file" 
                                bind:this={fileInput}
                                on:change={handleFileChange} 
                                accept="image/*" 
                                class="file-input"
                                disabled={loading}
                            />
                            {#if loading}
                                <div>                                    
                                    <div style="height: 60px; margin-bottom: 10px;" class="question">
                                        <Logotype />
                                    </div>
                                    <p style="margin-bottom: 10px; font-size: 0.85rem; color: #666;">Bearbetar bild...</p>
                                </div>
                            {:else if !isProOrPlus}
                                <div class="subscription-message">
                                    <p>För att ladda upp bilder behöver du ett konto med en prenumeration.</p>
                                    <Link to={subscriptionLinkTo} class="subscription-link" style="color: #2c3e50;">{isLoggedIn ? 'Gå till profil' : 'Logga in'}</Link>
                                </div>
                            {:else}
                                <button 
                                    class="upload-button"
                                    style="margin-bottom: 0px;"
                                    on:click={triggerFileInput}
                                    disabled={loading || hasUploaded}
                                >
                                    Ladda upp bild
                                </button>
                            {/if}
                            {#if error}
                                <p class="error-message">Fel: {error}</p>
                            {/if}
                        </div>
                    {/if}
                </div>
            </div>

            {#if $selectedItem?.commercial_area_sqm}
                <div class="metric-card">
                    <div class="metric-header">
                        <h6><i class="fa-solid fa-ruler-combined fa-sm icon-detail"></i> Total lokalyta</h6>
                    </div>
                    <div class="metric-value">
                        { displayInt($selectedItem.commercial_area_sqm) } kvm
                    </div>
                </div>
            {/if}

            {#if $selectedItem?.taxerings_value}
                <div class="metric-card">
                    <div class="metric-header">
                        <h6>Taxeringsvärde</h6>
                    </div>
                    <div class="metric-value">
                        { displayInt($selectedItem.taxerings_value) } kr
                    </div>
                </div>
            {/if}

            {#if $selectedItem?.assessed_value_of_the_land}
                <div class="metric-card">
                    <div class="metric-header">
                        <h6>Taxeringsvärde mark</h6>
                    </div>
                    <div class="metric-value">
                        { displayInt($selectedItem.assessed_value_of_the_land) } kr
                    </div>
                </div>
            {/if}

            {#if $selectedItem?.land_area}
                <div class="metric-card">
                    <div class="metric-header">
                        <h6><i class="fa-solid fa-ruler-combined fa-sm icon-detail"></i> Total markyta</h6>
                    </div>
                    <div class="metric-value">
                        { displayInt($selectedItem.land_area) } kvm
                    </div>
                </div>
            {/if}

            {#if $selectedItem?.rent_for_land != null}
                <div class="metric-card">
                    <div class="metric-header">
                        <h6>Hyra för mark</h6>
                    </div>
                    <div class="metric-value">
                        { displayInt($selectedItem.rent_for_land) } kr
                    </div>
                </div>
            {/if}

            {#if $selectedItem?.nybyggnadsar}
                <div class="metric-card">
                    <div class="metric-header">
                        <h6><i class="fa-solid fa-calendar-days fa-sm icon-detail"></i> Byggår</h6>
                    </div>
                    <div class="metric-value">
                        { $selectedItem.nybyggnadsar }
                    </div>
                </div>
            {/if}

            {#if $selectedItem?.byggnadstyp}
                <div class="metric-card">
                    <div class="metric-header">
                        <h6>Byggnadstyp</h6>
                    </div>
                    <div class="metric-value">
                        { $selectedItem.byggnadstyp }
                    </div>
                </div>
            {/if}

            {#if $selectedItem?.number_of_rental_apartments != null}
                <div class="metric-card">
                    <div class="metric-header">
                        <h6><i class="fa-solid fa-building fa-sm icon-detail"></i> Antal hyresrätter</h6>
                    </div>
                    <div class="metric-value">
                        { displayOptionalInt($selectedItem.number_of_rental_apartments) } st
                    </div>
                </div>
            {/if}

            {#if $selectedItem?.number_of_parking_spots != null}
                <div class="metric-card">
                    <div class="metric-header">
                        <h6><i class="fa-solid fa-square-parking fa-sm icon-detail"></i> Antal P-platser</h6>
                    </div>
                    <div class="metric-value">
                        { displayOptionalInt($selectedItem.number_of_parking_spots) } st
                    </div>
                </div>
            {/if}

            {#if $selectedItem?.raw_attic != null}
                <div class="metric-card">
                    <div class="metric-header">
                        <h6><i class="fa-solid fa-house-chimney fa-sm icon-detail"></i> Råvind finns</h6>
                    </div>
                    <div class="metric-value">
                        { displayOptionalBoolean($selectedItem.raw_attic) }
                    </div>
                </div>
            {/if}

            {#if $selectedItem?.bike_storage != null}
                <div class="metric-card">
                    <div class="metric-header">
                        <h6><i class="fa-solid fa-bicycle fa-sm icon-detail"></i> Cykelrum finns</h6>
                    </div>
                    <div class="metric-value">
                        { displayOptionalBoolean($selectedItem.bike_storage) }
                    </div>
                </div>
            {/if}

            {#if $selectedItem?.energy_addresses && $selectedItem.energy_addresses.length > 0}
                <div class="addresses-section">
                    <div class="metric-header">
                        <h6><i class="fa-solid fa-map-location-dot fa-sm icon-detail"></i> Adresser</h6>
                    </div>
                    <ul class="addresses-list">
                        {#each $selectedItem.energy_addresses as addressObj}
                            <li class:main-address={addressObj.main_address}>
                                {#if addressObj.main_address}
                                    <i class="fa-solid fa-star fa-xs" style="color: #ec776a; margin-right: 0.5rem;"></i>
                                {/if}
                                {addressObj.address}
                            </li>
                        {/each}
                    </ul>
                </div>
            {/if}
        </div>
    </div>
</div>

<style>
    .building-wrapper {
        width: 100%;
    }

    .building-box {
        background: white;
        backdrop-filter: blur(30px) saturate(120%);
        -webkit-backdrop-filter: blur(30px) saturate(120%);
        border-radius: 24px;
        padding: 1.5rem;
        border: none;
        box-shadow: 
            0 8px 32px rgba(0, 0, 0, 0.06),
            inset 0 1px 0 rgba(255, 255, 255, 0.3),
            inset 0 -1px 0 rgba(255, 255, 255, 0.1),
            0 0 0 1px rgba(255, 255, 255, 0.15);
    }

    .building-content-grid {
        display: grid;
        grid-template-columns: repeat(4, 1fr);
        gap: 20px;
        align-items: start;
    }

    .metric-card {
        text-align: left;
    }

    .metric-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 12px;
    }

    .metric-header h6 {
        margin: 0;
        font-size: 0.9rem;
        color: #666;
        font-weight: 500;
    }

    .image-section-center {
        grid-column: 2 / 4;
        grid-row: 1 / 3;
        display: flex;
        align-items: center;
        justify-content: center;
        overflow: hidden;
    }

    .image-container {
        width: 100%;
        max-height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        background: transparent;
        backdrop-filter: none;
        -webkit-backdrop-filter: none;
        border-radius: 0;
        border: none;
        padding: 0;
        box-shadow: none;
    }

    .image-container img {
        max-width: 100%;
        max-height: 100%;
        width: auto;
        height: auto;
        border-radius: 12px;
        display: block;
        object-fit: contain;
    }

    @media (max-width: 1200px) {
        .building-content-grid {
            grid-template-columns: 1fr;
            gap: 20px;
        }

        .metric-total-boyta,
        .metric-antal-lagenheter {
            grid-column: 1;
        }

        .metric-total-boyta {
            grid-row: 1;
        }

        .metric-antal-lagenheter {
            grid-row: 2;
        }

        .image-section-center {
            grid-column: 1;
            grid-row: 3;
        }
    }

    @media (max-width: 992px) {
        .building-box {
            padding: 1.25rem;
        }

        .building-content-grid {
            gap: 1.5rem;
        }

        .image-container {
            min-height: 200px;
        }
    }

    .addresses-section {
        grid-column: 1 / -1;
        margin-top: 1rem;
        padding-top: 1.5rem;
        border-top: 1px solid rgba(0, 0, 0, 0.08);
    }

    .addresses-list {
        list-style: none;
        padding: 0;
        margin: 0;
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
        gap: 0.75rem;
    }

    .addresses-list li {
        padding: 0.75rem 1rem;
        background: rgba(0, 0, 0, 0.02);
        border-radius: 8px;
        font-size: 0.9rem;
        color: #333;
        border: 1px solid rgba(0, 0, 0, 0.05);
        display: flex;
        align-items: center;
    }

    .addresses-list li.main-address {
        background: rgba(236, 119, 106, 0.08);
        border-color: rgba(236, 119, 106, 0.2);
        font-weight: 500;
    }

    @media (max-width: 1200px) {
        .addresses-list {
            grid-template-columns: 1fr;
        }
    }

    .subscription-message {
        text-align: center;
        padding: 1.5rem;
        background: rgba(236, 119, 106, 0.08);
        border: 1px solid rgba(236, 119, 106, 0.2);
        border-radius: 12px;
        max-width: 300px;
    }

    .subscription-message p {
        margin: 0 0 1rem 0;
        font-size: 0.9rem;
        color: #333;
        line-height: 1.5;
    }

    :global(.subscription-link),
    :global(a.subscription-link) {
        display: inline-block !important;
        padding: 0.6rem 1.2rem !important;
        background: linear-gradient(135deg, rgba(255, 255, 255, 0.25) 0%, rgba(255, 255, 255, 0.15) 100%) !important;
        backdrop-filter: blur(15px) saturate(120%) !important;
        -webkit-backdrop-filter: blur(15px) saturate(120%) !important;
        color: #2c3e50 !important;
        text-decoration: none !important;
        border: none !important;
        border-radius: 12px !important;
        font-size: 0.9rem !important;
        font-weight: 600 !important;
        transition: transform 0.2s, box-shadow 0.2s !important;
        box-shadow: 
            0 4px 16px rgba(0, 0, 0, 0.06),
            inset 0 1px 0 rgba(255, 255, 255, 0.3),
            inset 0 -1px 0 rgba(255, 255, 255, 0.1),
            0 0 0 1px rgba(255, 255, 255, 0.15) !important;
    }

    :global(.subscription-link:hover),
    :global(a.subscription-link:hover) {
        transform: scale(1.05) !important;
        text-decoration: none !important;
        color: #1a252f !important;
        box-shadow: 
            0 6px 20px rgba(0, 0, 0, 0.08),
            inset 0 1px 0 rgba(255, 255, 255, 0.4),
            inset 0 -1px 0 rgba(255, 255, 255, 0.15),
            0 0 0 1px rgba(255, 255, 255, 0.2) !important;
    }
</style>

